import {BehaviorSubject, Subscription} from 'rxjs';
import configModel from 'gmfapi/store/config';
import {IProduct} from './geoshop_api';
import {User, UserManager, ErrorResponse} from 'oidc-client-ts';
import Geometry from 'ol/geom/Geometry';
import GeoJSON from 'ol/format/GeoJSON';

export class AuthorizationError extends Error {}
export class NotConnectedError extends AuthorizationError {}
export class NotAuthorizedError extends AuthorizationError {}

type redirectOptionsType = {
  forceSelect?: boolean;
  redirectUrl?: string;
};

export class Geoshop {
  silentTimeout_ = 5; // seconds till timeout
  refreshPeriod_ = 120000; // 2 min

  ready: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  user: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  tokens: BehaviorSubject<{access: string; refresh: string} | null> = new BehaviorSubject<{
    access: string;
    refresh: string;
  } | null>(null);
  // use test platform by default
  private geoshopApiUrl_: string = 'https://ugsp-geoshop-test.camptocamp.com/api/';
  private baseUrl_: string = "'https://localhost:3002/";

  private refreshTokenInterval: ReturnType<typeof setInterval> | null = null;
  private userManager: UserManager | null = null;
  private interactiveLoginNeeded: boolean = false;

  private login_when_ready: Subscription | null = null;

  constructor() {
    this.setBaseUrl();

    configModel.getConfig().subscribe((config) => {
      if (!config) {
        return;
      }
      if (!this.userManager) {
        const userManagerSettings = {
          authority: config.oidcIssuerUrl,
          client_id: config.oidcClientId,
          redirect_uri: `${this.baseUrl_}/login_callback.html`,
          popup_redirect_uri: `${this.baseUrl_}/login_callback.html?redirect_from=popup`,
          silent_redirect_uri: `${this.baseUrl_}/login_callback.html?redirect_from=silent`,

          post_logout_redirect_uri: `${this.baseUrl_}/login_callback.html?redirect_from=logout`,
          response_type: 'code',
          loadUserInfo: true,
          scope: 'openid email profile phone offline_access',
        };
        this.userManager = new UserManager(userManagerSettings);
        this.ready.next(true);
      }
    });
  }

  loginWhenReady() {
    if (this.login_when_ready) {
      this.login_when_ready.unsubscribe();
    }
    this.login_when_ready = this.ready.subscribe(async (ready) => {
      if (ready) {
        const user = await this.getUser();
        if (!user) {
          await this.iframeLogin();
          await this.getUser();
          this.login_when_ready.unsubscribe();
        }
      }
    });
  }

  setBaseUrl() {
    const url_parts = window.location.href.split('/');
    // found no better way than handling the theme path via a dedicated condition
    if (url_parts[url_parts.length - 2] == 'theme') {
      this.baseUrl_ = url_parts.slice(0, url_parts.length - 2).join('/');
    } else {
      this.baseUrl_ = url_parts.slice(0, url_parts.length - 1).join('/');
    }
  }

  setApiUrl(url: string) {
    this.geoshopApiUrl_ = url;
  }

  async fetchWithApiBase(suffix: string, options?: any) {
    const fullUrl = `${this.geoshopApiUrl_}${suffix}`;
    return this.fetchWithApi(fullUrl, options);
  }

  async fetchWithApi(path: string, options?: any) {
    const authOptions = {headers: {}, ...(options || {})};
    if (this.tokens.value?.access) {
      authOptions.headers['Authorization'] = `Bearer ${this.tokens.value?.access}`;
    }
    let response = await fetch(path, authOptions);
    try {
      if (response.status === 401) {
        throw new Error('Not connected');
      }
      if (response.status === 403) {
        throw new Error('Not authorized');
      }
    } catch {
      console.log('Not logged in - trying to log in.');
      await geoshop.orderLogin();
      await geoshop.getUser();
      if (this.tokens.value?.access) {
        console.log('getting token' + this.tokens.value?.access);
        authOptions.headers['Authorization'] = `Bearer ${this.tokens.value?.access}`;
      }
      response = await fetch(path, authOptions);
    }
    return response;
  }

  connect(user: User | null | undefined) {
    if (user) {
      this.user.next(user);
      const user_data = JSON.stringify(user);
      localStorage.setItem('user_info', user_data);
    }
  }

  async backend_connect(user: User | null | undefined) {
    if (user) {
      const tokenUrl = `${this.geoshopApiUrl_}oidc/token`;
      console.log('fetch Token');
      const resp = await fetch(tokenUrl, {
        method: 'POST',
        body: JSON.stringify({token: user.access_token}),
      });
      const json = await resp.text();
      this.tokens.next(JSON.parse(json));
      localStorage.setItem('backend_tokens', json);
      if (this.refreshTokenInterval !== null) {
        clearInterval(this.refreshTokenInterval);
      }
      this.refreshTokenInterval = setInterval(() => this.refreshToken(), this.refreshPeriod_);
    } else {
      console.log('no user');
    }
  }

  async refreshToken() {
    const refreshUrl = `${this.geoshopApiUrl_}token/refresh/`;
    const resp = await fetch(refreshUrl, {
      method: 'POST',
      headers: {'content-type': 'application/json'},
      body: JSON.stringify({
        refresh: this.tokens.value!.refresh,
      }),
    });
    const json = await resp.json();
    const new_tokens = {refresh: this.tokens.value!.refresh, access: json.access};
    this.tokens.next(new_tokens);
  }

  async iframeLogin() {
    try {
      console.log('BG iframe login');
      const user = await this.userManager?.signinSilent({
        silentRequestTimeoutInSeconds: this.silentTimeout_,
        prompt: 'none',
      });
      return user;
    } catch (err) {
      if (err instanceof ErrorResponse) {
        if (err.error == 'interaction_required') {
          this.interactiveLoginNeeded = true;
          console.log('BG iframe login failed, interaction required for SSO login');
        } else {
          console.error(err.message);
        }
      }
      return undefined;
    }
  }

  async popupLogin() {
    try {
      console.log('BG popup login');
      const user = await this.userManager?.signinPopup({
        popupWindowFeatures: {closePopupWindowAfterInSeconds: this.silentTimeout_},
        prompt: this.interactiveLoginNeeded ? 'select_account' : 'none',
      });
      return user;
    } catch (err) {
      if (err instanceof ErrorResponse) {
        if (err.error == 'interaction_required') {
          this.interactiveLoginNeeded = true;
          console.log('BG popup login failed, interaction required for SSO login');
        } else {
          console.error(err.message);
        }
      }
      return undefined;
    }
  }

  async orderLogin() {
    const user = await geoshop.getUser();
    if (!user) {
      await this.login({forceSelect: true});
    }
  }

  async login(options?: redirectOptionsType) {
    try {
      await this.userManager?.signinPopup({
        prompt: options?.forceSelect || this.interactiveLoginNeeded ? 'select_account' : 'none',
      });
    } catch (err) {
      if (err instanceof ErrorResponse && err.error == 'interaction_required') {
        this.interactiveLoginNeeded = true;
        console.log('automatic redirect login failed, interaction required for SSO login');
      } else if (err instanceof Error) {
        if (err.message.includes('disposed window')) {
          console.log('Popup windows blocked');
          // a bit noisy fallback solution in which redirects are done in the main window
          const redirectOptions: redirectOptionsType = {...options};
          if (!options?.redirectUrl) {
            const url = new URL(window.location.href);
            url.searchParams.set('geoshop', '');
            redirectOptions.redirectUrl = url.toString();
          }
          this.loginRedirect(redirectOptions);
        }
      }
    }
  }

  async loginRedirect(options?: redirectOptionsType) {
    if (options?.redirectUrl) {
      const params = new URLSearchParams({
        redirect_to: options?.redirectUrl,
      });
      console.log('redirect login');
      await this.userManager?.signinRedirect({
        redirect_uri: `${this.baseUrl_}/login_callback.html?${params.toString()}`,
        prompt: options?.forceSelect || this.interactiveLoginNeeded ? 'select_account' : 'none',
      });
    } else {
      await this.userManager?.signinRedirect({
        prompt: options?.forceSelect || this.interactiveLoginNeeded ? 'select_account' : 'none',
      });
    }
  }

  async getUser() {
    console.log('get user');
    const user = await this.userManager?.getUser();
    if (user) this.connect(user);
    await this.backend_connect(user);
    return user;
  }

  async logout() {
    // todo: logout and revoke token from backend
    // geoshop frontend seems to only clear the session, latest token remains valid until expiry
    // await this.userManager.revokeTokens();
    localStorage.removeItem('user_info');
    localStorage.removeItem('backend_tokens');
    this.user.next(null);
    console.log('signout');
    // await this.userManager?.removeUser();
    await this.userManager?.signoutSilent().catch((err) => console.log(err));
  }

  async callback() {
    console.log('callback');
    try {
      const user = (await this.userManager?.signinCallback()) || null;
      this.connect(user);
      await this.backend_connect(user);
    } catch (err) {
      if (err instanceof ErrorResponse && err.error == 'interaction_required') {
        this.interactiveLoginNeeded = true;
        console.log('automatic redirect login failed, interaction required for SSO login');
      } else if (
        err instanceof Error &&
        (err.message.includes('No state in response') ||
          err.message.includes('No matching state found in storage'))
      ) {
        console.log('No state found => not logged in');
      } else {
        console.error('Unexpected Error in callback => not logged in');
      }
    }
  }

  async getProducts() {
    const productUrl = `${this.geoshopApiUrl_}product/`;
    const response = await fetch(productUrl, {
      method: 'GET',
      mode: 'cors',
      headers: {
        // "X-PINGOTHER": "pingpong",
      },
    });
    return await response.json();
  }

  async getOrder(item_no: number) {
    const item_url = `${this.geoshopApiUrl_}order/${item_no}/`;
    const last_order_item = await this.fetchWithApi(item_url);
    return await last_order_item.json();
  }

  async sendOrder(products: IProduct[], extent: Geometry, isPrivate: boolean) {
    const orderUrl = `${this.geoshopApiUrl_}order/`;
    const timestamp = new Date();
    const date_string = timestamp.toLocaleString('de-CH');
    const order = {
      'description': 'Geoviewer Order',
      'geom': new GeoJSON().writeGeometry(extent),
      'invoice_reference': '',
      'email_deliver': '',
      'order_type': isPrivate ? 'private' : 'public',
      'title': `LKGR ${date_string}`,
      'items': products.map((product) => ({
        'product': {
          'label': product.label,
        },
        'product_id': product.id,
      })),
    };
    const options = {
      'method': 'POST',
      'body': JSON.stringify(order),
      headers: {
        'Content-Type': 'application/json',
      },
    };
    if (this.tokens.value?.access) {
      console.log('getting first token' + this.tokens.value?.access);
      options.headers['Authorization'] = `Bearer ${this.tokens.value?.access}`;
    }

    const response = await this.fetchWithApi(orderUrl, options);

    // if (response.status === 401) {
    //   throw new NotConnectedError("Not connected");
    // }
    // if (response.status === 403) {
    //   throw new NotAuthorizedError("Not authorized");
    // }
    if (response.status === 201) {
      const order = await response.json();
      const item_no = order.id;
      const item_url = `${this.geoshopApiUrl_}order/${item_no}/`;
      const order_item = await this.fetchWithApi(item_url);
      return await order_item.json();
    }
    return {};
  }

  async getOrders() {
    const orderUrl = `${this.geoshopApiUrl_}order/`;
    const response = await this.fetchWithApi(orderUrl);
    return (await response.json()).results;
  }

  async getLastOrder() {
    const orders = await this.getOrders();
    const last_order = orders[0];
    const item_no = last_order.url.split('/').slice(-2)[0];
    return await this.getOrder(item_no);
  }

  createDraftOrder() {}

  confirmOrder() {}
}

const geoshop = new Geoshop();
export default geoshop;
